import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Application, useApplication, useEntityHistory } from '~/data';
import {
  EntityHistoryEntry,
  EntityHistoryMap,
  HistoryRecord,
  getAccountTypeDisplayName,
} from '~/data/useEntityHistory';
import { useEducationHistoryTimestamps } from '~/data/useEducationHistoryTimestamps';
import { useSchoolCustomizations } from '~/data/useSchoolCustomizations';
import { useRelationshipOptions } from '~/data/useRelationshipOptions';
import { Flex, Spinner } from '@applyboard/crystal-ui';
import { ApplicationDetailsLayout } from './layouts';
import { useWindowDocumentTitle } from '~/lib/useWindowDocumentTitle';
import { ApplicationDetailContext } from './context';
import { ErrorHandler, ErrorPage } from '../ErrorPage';
import { useCourses } from '~/data/useCourses';

/** Exported for unit tests. Picks the entry with the latest updatedAt. */
export function getMostRecentEntry(...entries: (EntityHistoryEntry | undefined)[]): EntityHistoryEntry | undefined {
  const valid = entries.filter((e): e is EntityHistoryEntry => Boolean(e?.updatedAt));
  if (valid.length === 0) return undefined;
  return valid.reduce(
    (latest, current) => (new Date(current.updatedAt) > new Date(latest.updatedAt) ? current : latest),
    valid[0]
  );
}

type TranscriptDocShape = { id: string; attributes: { extractedOn?: string; updatedAt?: string; createdAt?: string } };

/**
 * Returns the document ID for each education history item.
 * For transcripts: uses the latest transcript extraction's ID.
 * For certificates (no extractions): falls back to the first document's key.
 */
function getEducationHistoryDocumentIds(application: Application | undefined): string[] {
  const items = application?.educationHistories?.educationHistoryItems;
  if (!items?.length) return [];

  return items
    .map((item) => {
      const transcripts = (item.transcriptExtractions ?? []) as TranscriptDocShape[];
      if (transcripts.length) {
        const sorted = [...transcripts].sort((a, b) => {
          const getTime = (doc: TranscriptDocShape) =>
            new Date(doc.attributes.extractedOn ?? doc.attributes.updatedAt ?? doc.attributes.createdAt ?? 0).getTime();
          return getTime(b) - getTime(a);
        });
        return sorted[0]?.id ?? '';
      }

      return item.documents?.[0]?.key ?? '';
    })
    .filter(Boolean);
}

/**
 * Extracts per-education-history-key timestamps from application history records.
 * Parses change keys like "educationHistory.{ehKey}.fieldName" to identify
 * the most recent update for each EHI key.
 */
export function buildEhKeyTimestamps(records: HistoryRecord[]): Record<string, EntityHistoryEntry> {
  const EH_CHANGE_PREFIX = 'educationHistory.';
  const map: Record<string, EntityHistoryEntry> = {};

  for (const record of records) {
    if (record.attributes.referenceId !== 'educationHistory') continue;

    const updatedAt = record.meta.updatedAt;
    const updatedByRole = getAccountTypeDisplayName(record.attributes.accountType ?? undefined);

    for (const changeKey of Object.keys(record.attributes.changes)) {
      if (!changeKey.startsWith(EH_CHANGE_PREFIX)) continue;
      const rest = changeKey.slice(EH_CHANGE_PREFIX.length);
      const dotIndex = rest.indexOf('.');
      const ehKey = dotIndex > 0 ? rest.slice(0, dotIndex) : rest;
      if (!ehKey) continue;

      const existing = map[ehKey];
      if (!existing || new Date(updatedAt) > new Date(existing.updatedAt)) {
        map[ehKey] = { updatedAt, updatedByRole };
      }
    }
  }

  return map;
}

export function ApplicationDetailsPage() {
  useWindowDocumentTitle('Application Details');
  const applicationId = useApplicationId();
  const { application, isLoadingApplication, applicationError, isApplicationError } = useApplication(applicationId);
  const {
    schoolCustomizations,
    isLoadingSchoolCustomizations,
    isSchoolCustomizationsError,
    schoolCustomizationsError,
  } = useSchoolCustomizations();
  const { courses, isLoadingCourses, isCoursesError } = useCourses();
  const { relationshipOptions } = useRelationshipOptions();

  const passportId = application?.statusAndCitizenship?.passportId ?? '';
  const studentId = application?.studentPersonaInformation?.id ?? '';
  const educationHistoryDocIds = useMemo(() => getEducationHistoryDocumentIds(application), [application]);

  const { data: applicationHistory } = useEntityHistory('application', applicationId);
  const { data: passportHistory } = useEntityHistory('passport', passportId);
  const { data: studentHistory } = useEntityHistory('student', studentId);
  const { data: educationHistoryTimestamps } = useEducationHistoryTimestamps(
    educationHistoryDocIds,
    applicationHistory?.map?.educationHistory
  );

  const ehKeyTimestamps = useMemo(
    () => (applicationHistory?.records ? buildEhKeyTimestamps(applicationHistory.records) : {}),
    [applicationHistory?.records]
  );

  const entityHistoryMap: EntityHistoryMap | undefined = useMemo(() => {
    const appMap = applicationHistory?.map;
    const passMap = passportHistory?.map;
    const stuMap = studentHistory?.map;

    if (!appMap && !passMap && !stuMap) {
      return undefined;
    }

    const map: EntityHistoryMap = {};

    if (appMap?.contactInformation) {
      map.contactInformation = appMap.contactInformation;
    }
    if (appMap?.addresses) {
      map.addresses = appMap.addresses;
    }

    const personalInfoEntry = getMostRecentEntry(
      appMap?.personalInformation,
      stuMap?.personalInformation,
      passMap?.personalInformation
    );
    if (personalInfoEntry) {
      map.personalInformation = personalInfoEntry;
    }

    return map;
  }, [applicationHistory, passportHistory, studentHistory]);

  if (
    !isApplicationError &&
    !isSchoolCustomizationsError &&
    !isCoursesError &&
    (isLoadingApplication || isLoadingSchoolCustomizations || isLoadingCourses)
  ) {
    return (
      <Flex p={5} gap={5} align="center" direction="column" role="progressbar">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (isApplicationError) {
    return <ErrorHandler error={applicationError} customMessage="Failed to fetch the application details." />;
  }

  if (isSchoolCustomizationsError) {
    return <ErrorHandler error={schoolCustomizationsError} customMessage="Failed to fetch school customizations." />;
  }

  if (!application || !schoolCustomizations || !courses) {
    return <ErrorPage customMessage="Failed to fetch page props." />;
  }
  return (
    <ApplicationDetailContext
      application={application}
      schoolCustomizations={schoolCustomizations}
      courses={courses}
      relationshipOptions={relationshipOptions}
      entityHistoryMap={entityHistoryMap}
      educationHistoryTimestamps={educationHistoryTimestamps}
      ehKeyTimestamps={ehKeyTimestamps}
    >
      <ApplicationDetailsLayout />
    </ApplicationDetailContext>
  );
}

function useApplicationId() {
  const { id } = useParams() as { id: string };
  return id;
}
